import './Admin.scss';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Backup from './menus/backup/Backup';
import Inquiries from './menus/inquiries/Inquiries';
import PrepaidList from './menus/prepaid-list/PrepaidList';
import PaymentPendingList from './menus/payment-pending-list/PaymentPendingList';
import Dashboard from './menus/dashboard/Dashboard';
import ChurchList from './menus/church-list/ChurchList';
import AppManagementPage from './menus/app-management-page/AppManagementPage';
import AreaManagementPage from './menus/area-management-page/AreaManagementPage';
import NoticeAuthoring from './menus/notice-authoring/NoticeAuthoring';
import FaqArchive from './menus/faq-archive/FaqArchive';
import HolidayManagement from './menus/holiday-management/HolidayManagement';
import SupportTickets from './menus/support-tickets/SupportTickets';
import AddonPurchases from './menus/addon-purchases/AddonPurchases';
import TaxInvoices from './menus/tax-invoices/TaxInvoices';
import PlanRenewal from './menus/plan-renewal/PlanRenewal';
import MemberLimitPage from './menus/member-limit/MemberLimitPage';
import StaffPermissionsPage from './menus/staff-permissions-page/StaffPermissionsPage';
import AuditLogPage from './menus/audit-log-page/AuditLogPage';
import SiteStats from './menus/site-stats/SiteStats';
import AdminComingSoon from './AdminComingSoon';
import { clearAdminSession, getAdminDisplayName, getAdminSession, isSuperAdmin } from './adminSession';

type MenuKey =
  | 'dashboard'
  | 'siteStats'
  | 'inquiry'
  | 'prepaidList'
  | 'paymentPendingList'
  | 'churchList'
  | 'appManagement'
  | 'areaManagement'
  | 'holidays'
  | 'themeTemplates'
  | 'churchBranding'
  | 'featureToggle'
  | 'bulletinScheduler'
  | 'pushDelivery'
  | 'apiIntegrations'
  | 'planRenewal'
  | 'memberLimit'
  | 'addonPurchases'
  | 'taxInvoice'
  | 'supportTickets'
  | 'noticeAuthoring'
  | 'faqArchive'
  | 'staffPermissions'
  | 'auditLog'
  | 'backup';

type MenuGroupKey =
  | 'stats'
  | 'applications'
  | 'churchClients'
  | 'appCommon'
  | 'designTheme'
  | 'featureModules'
  | 'billing'
  | 'cs'
  | 'systemSecurity'
  | 'backup';

type MenuItem = {
  key: MenuKey;
  label: string;
  adminOnly?: boolean;
  superOnly?: boolean;
  icon: string;
};

type MenuGroup = {
  key: MenuGroupKey;
  label: string;
  icon: string;
  childKeys: MenuKey[];
  bottom?: boolean;
};

const MENU_ITEMS: MenuItem[] = [
  { key: 'dashboard', label: '대시보드', icon: '📊' },

  // 01-0. 통계
  { key: 'siteStats', label: '웹사이트 방문자', icon: '📈' },

  // 01-1. 신청목록
  { key: 'inquiry', label: '신청 목록', icon: '📩' },
  { key: 'prepaidList', label: '선결제 목록', icon: '💰' },
  { key: 'paymentPendingList', label: '결제대기 목록', icon: '⏳' },

  // 02. 등록교회관리
  { key: 'churchList', label: '등록 교회 목록', icon: '⛪' },
  { key: 'appManagement', label: '앱관리', adminOnly: true, icon: '📲' },
  { key: 'areaManagement', label: 'area 관리', adminOnly: true, icon: '🗂️' },

  // 02-1. 앱 공통 관리 (모든 교회 앱에 공통으로 나가는 데이터)
  { key: 'holidays', label: '공휴일 관리', icon: '🗓️' },

  // 03. 디자인 & 테마 세팅 (메뉴 비활성화 - 주석처리)
  // { key: 'themeTemplates', label: '디자인 템플릿 관리', icon: '🎨' },
  // { key: 'churchBranding', label: '로고 · 브랜드컬러', icon: '🖌️' },
  // { key: 'featureToggle', label: '메뉴 기능 On/Off', icon: '🧩' },

  // 04. 기능 및 서비스 모듈 (메뉴 비활성화 - 주석처리)
  // { key: 'bulletinScheduler', label: '주보 스케줄러 모니터링', icon: '🗓️' },
  // { key: 'pushDelivery', label: '푸시 발송 상태', icon: '🔔' },
  // { key: 'apiIntegrations', label: '외부 연동 점검', icon: '🔌' },

  // 05. 결제 및 정산 관리
  { key: 'planRenewal', label: '요금제 · 갱신 현황', icon: '💳' },
  { key: 'memberLimit', label: '성도 인원 한도', icon: '👥' },
  { key: 'addonPurchases', label: '부가 옵션 구매 내역', icon: '➕' },
  { key: 'taxInvoice', label: '세금계산서 · 영수증', icon: '🧾' },

  // 06. CS 및 고객지원
  { key: 'supportTickets', label: '사역자 요청 티켓', icon: '🎫' },
  { key: 'noticeAuthoring', label: '공지사항 관리', icon: '📝' },
  { key: 'faqArchive', label: '자주 묻는 질문 관리', icon: '📚' },

  // 07. 시스템 & 보안 관리
  { key: 'staffPermissions', label: '관리자 권한 관리', adminOnly: true, icon: '🔐' },
  { key: 'auditLog', label: '작업 이력 (Audit Log)', adminOnly: true, icon: '🕵️' },

  { key: 'backup', label: '백업', adminOnly: true, icon: '🗄️' },
];

const MENU_GROUPS: MenuGroup[] = [
  {
    key: 'stats',
    label: '통계',
    icon: '📈',
    childKeys: ['siteStats'],
  },
  {
    key: 'applications',
    label: '신청목록',
    icon: '📩',
    childKeys: ['inquiry', 'prepaidList', 'paymentPendingList'],
  },
  {
    key: 'churchClients',
    label: '등록교회관리',
    icon: '⛪',
    childKeys: ['churchList', 'appManagement', 'areaManagement'],
  },
  {
    key: 'appCommon',
    label: '앱 공통 관리',
    icon: '🗓️',
    childKeys: ['holidays'],
  },
  // 메뉴 비활성화 - 주석처리
  // {
  //   key: 'designTheme',
  //   label: '디자인 & 테마 세팅',
  //   icon: '🎨',
  //   childKeys: ['themeTemplates', 'churchBranding', 'featureToggle'],
  // },
  // {
  //   key: 'featureModules',
  //   label: '기능 및 서비스 모듈',
  //   icon: '🧩',
  //   childKeys: ['bulletinScheduler', 'pushDelivery', 'apiIntegrations'],
  // },
  {
    key: 'billing',
    label: '결제 및 정산 관리',
    icon: '💳',
    childKeys: ['planRenewal', 'memberLimit', 'addonPurchases', 'taxInvoice'],
  },
  {
    key: 'cs',
    label: 'CS 및 고객지원',
    icon: '🎧',
    childKeys: ['supportTickets', 'noticeAuthoring', 'faqArchive'],
  },
  {
    key: 'systemSecurity',
    label: '시스템 & 보안 관리',
    icon: '🔐',
    childKeys: ['staffPermissions', 'auditLog'],
  },
  {
    key: 'backup',
    label: '백업',
    icon: '🗄️',
    childKeys: ['backup'],
  },
];

function canViewMenuItem(item: MenuItem, session: ReturnType<typeof getAdminSession>): boolean {
  if (item.superOnly) return isSuperAdmin(session);
  if (item.adminOnly) return isSuperAdmin(session);
  return true;
}

function AdminMainContent({
  activeMenu,
  adminSession,
}: {
  activeMenu: MenuKey;
  adminSession: ReturnType<typeof getAdminSession>;
}) {
  switch (activeMenu) {
    case 'dashboard':
      return <Dashboard />;
    case 'siteStats':
      return <SiteStats />;
    case 'inquiry':
      return <Inquiries />;
    case 'prepaidList':
      return <PrepaidList />;
    case 'paymentPendingList':
      return <PaymentPendingList />;
    case 'churchList':
      return <ChurchList />;
    case 'appManagement':
      return <AppManagementPage />;
    case 'areaManagement':
      return <AreaManagementPage />;
    case 'themeTemplates':
      return (
        <AdminComingSoon
          title="디자인 템플릿 관리"
          desc="기본 제공 디자인 테마 템플릿 자산을 관리합니다."
        />
      );
    case 'churchBranding':
      return (
        <AdminComingSoon
          title="로고 · 브랜드컬러"
          desc="교회별 로고와 브랜드 컬러(HEX)를 세팅합니다."
        />
      );
    case 'featureToggle':
      return (
        <AdminComingSoon
          title="메뉴 기능 On/Off"
          desc="교회 앱의 메인 탭 · 메뉴 노출 여부(헌금 기능 등)를 켜고 끕니다."
        />
      );
    case 'bulletinScheduler':
      return (
        <AdminComingSoon
          title="주보 스케줄러 모니터링"
          desc="모바일 주보 자동 발행 스케줄러의 작동 상태를 모니터링합니다."
        />
      );
    case 'pushDelivery':
      return (
        <AdminComingSoon
          title="푸시 발송 상태"
          desc="FCM / APNs 푸시 알림 서버 전송 상태를 확인하고 재발송합니다."
        />
      );
    case 'apiIntegrations':
      return (
        <AdminComingSoon
          title="외부 연동 점검"
          desc="유튜브 API, 결제대행사(PG) 등 외부 연동 상태를 점검합니다."
        />
      );
    case 'planRenewal':
      return <PlanRenewal />;
    case 'memberLimit':
      return <MemberLimitPage />;
    case 'addonPurchases':
      return <AddonPurchases />;
    case 'taxInvoice':
      return <TaxInvoices />;
    case 'supportTickets':
      return <SupportTickets />;
    case 'noticeAuthoring':
      return <NoticeAuthoring />;
    case 'faqArchive':
      return <FaqArchive />;
    case 'holidays':
      return <HolidayManagement />;
    case 'staffPermissions':
      return <StaffPermissionsPage />;
    case 'auditLog':
      return <AuditLogPage />;
    case 'backup':
      return <Backup />;
    default:
      return <Dashboard />;
  }
}

export default function Main(props: any) {
  const navigate = useNavigate();
  const adminSession = getAdminSession();
  const superAdmin = isSuperAdmin(adminSession);
  const [activeMenu, setActiveMenu] = useState<MenuKey>('dashboard');
  // 좁은 화면에서는 사이드 메뉴를 접어 두고 상단 '메뉴' 버튼으로 연다. 메뉴를 고르면 닫힌다.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<MenuGroupKey, boolean>>({
    stats: false,
    applications: false,
    churchClients: false,
    appCommon: false,
    designTheme: false,
    featureModules: false,
    billing: false,
    cs: false,
    systemSecurity: false,
    backup: false,
  });

  const visibleMenus = useMemo(
    () => MENU_ITEMS.filter((item) => canViewMenuItem(item, adminSession)),
    [adminSession]
  );

  useEffect(() => {
    const activeMenuVisible = visibleMenus.some((menu) => menu.key === activeMenu);
    if (!activeMenuVisible && visibleMenus.length > 0) {
      setActiveMenu(visibleMenus[0].key);
    }
  }, [activeMenu, visibleMenus]);

  useEffect(() => {
    const parentGroup = MENU_GROUPS.find((group) => group.childKeys.includes(activeMenu));
    if (!parentGroup) return;
    setOpenMenuGroups((prev) => ({ ...prev, [parentGroup.key]: true }));
  }, [activeMenu]);

  const visibleMenuMap = useMemo(
    () => new Map(visibleMenus.map((menu) => [menu.key, menu] as const)),
    [visibleMenus]
  );

  const groupedChildKeys = useMemo(
    () => new Set(MENU_GROUPS.flatMap((group) => group.childKeys)),
    []
  );

  const firstChildGroupMap = useMemo(() => {
    const map = new Map<MenuKey, MenuGroup>();
    MENU_GROUPS.forEach((group) => {
      const firstVisibleChild = group.childKeys.find((key) => visibleMenuMap.has(key));
      if (firstVisibleChild) map.set(firstVisibleChild, group);
    });
    return map;
  }, [visibleMenuMap]);

  const toggleMenuGroup = (groupKey: MenuGroupKey) => {
    setOpenMenuGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const normalVisibleMenus = useMemo(
    () => visibleMenus.filter((menu) => !MENU_GROUPS.some((group) => group.bottom && group.childKeys.includes(menu.key))),
    [visibleMenus]
  );

  const bottomGroups = useMemo(() => MENU_GROUPS.filter((group) => group.bottom), []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeMenu]);

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin');
    window.scrollTo(0, 0);
  };

  return (
    <div className="AdminContainer admin-main-shell">
      <header className="admin-main-shell__topbar">
        <div className="admin-main-shell__brand-wrap">
          <button
            type="button"
            className="admin-main-shell__menu-toggle"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? '닫기' : '메뉴'}
          </button>
          <div className="admin-main-shell__brand">교회앱모아 관리자</div>
        </div>
        <div className="admin-main-shell__user">
          <button
            type="button"
            className="admin-main-shell__home-btn"
            onClick={() => navigate('/')}
          >
            홈
          </button>
          <button
            type="button"
            className="admin-main-shell__home-btn admin-main-shell__logout-btn"
            onClick={handleLogout}
          >
            로그아웃
          </button>
          <span className="admin-main-shell__user-name">
            {getAdminDisplayName(adminSession) || 'admin'}
          </span>
          <span className="admin-main-shell__user-role">
            {superAdmin ? '최종관리자' : '관리자'}
          </span>
        </div>
      </header>

      <div className="admin-main-layout">
        <aside className={`admin-main-layout__sidebar${mobileMenuOpen ? ' is-open' : ''}`}>
          <div className="admin-main-layout__sidebar-title">관리자 메뉴</div>
          <nav className="admin-main-layout__menu">
            {normalVisibleMenus.map((menu) => {
              const group = firstChildGroupMap.get(menu.key);
              if (group) {
                const childMenus = group.childKeys
                  .map((key) => visibleMenuMap.get(key))
                  .filter((item): item is MenuItem => Boolean(item));
                if (childMenus.length === 0) return null;

                const isOpen = openMenuGroups[group.key];
                const isGroupActive = childMenus.some((child) => child.key === activeMenu);

                return (
                  <div key={group.key} className="admin-main-layout__menu-group">
                    <button
                      type="button"
                      className={`admin-main-layout__menu-btn admin-main-layout__menu-parent${
                        isGroupActive ? ' is-active' : ''
                      }`}
                      onClick={() => toggleMenuGroup(group.key)}
                    >
                      <span className="admin-main-layout__menu-icon" aria-hidden>{group.icon}</span>
                      <span>{group.label}</span>
                      <span className={`admin-main-layout__menu-caret${isOpen ? ' is-open' : ''}`}>▾</span>
                    </button>
                    {isOpen && (
                      <div className="admin-main-layout__submenu">
                        {childMenus.map((childMenu) => (
                          <button
                            key={childMenu.key}
                            type="button"
                            className={`admin-main-layout__menu-btn admin-main-layout__submenu-btn${
                              activeMenu === childMenu.key ? ' is-active' : ''
                            }`}
                            onClick={() => {
                              window.scrollTo(0, 0);
                              setActiveMenu(childMenu.key);
                            }}
                          >
                            <span>{childMenu.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              if (groupedChildKeys.has(menu.key)) return null;

              return (
                <button
                  key={menu.key}
                  type="button"
                  className={`admin-main-layout__menu-btn${activeMenu === menu.key ? ' is-active' : ''}`}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    setActiveMenu(menu.key);
                  }}
                >
                  <span className="admin-main-layout__menu-icon" aria-hidden>{menu.icon}</span>
                  <span>{menu.label}</span>
                </button>
              );
            })}

            {bottomGroups.map((group) => {
              const childMenus = group.childKeys
                .map((key) => visibleMenuMap.get(key))
                .filter((item): item is MenuItem => Boolean(item));
              if (childMenus.length === 0) return null;

              const isOpen = openMenuGroups[group.key];
              const isGroupActive = childMenus.some((child) => child.key === activeMenu);

              return (
                <div key={group.key} className="admin-main-layout__menu-group admin-main-layout__menu-group--bottom">
                  <button
                    type="button"
                    className={`admin-main-layout__menu-btn admin-main-layout__menu-parent${
                      isGroupActive ? ' is-active' : ''
                    }`}
                    onClick={() => toggleMenuGroup(group.key)}
                  >
                    <span className="admin-main-layout__menu-icon" aria-hidden>{group.icon}</span>
                    <span>{group.label}</span>
                    <span className={`admin-main-layout__menu-caret${isOpen ? ' is-open' : ''}`}>▾</span>
                  </button>
                  {isOpen && (
                    <div className="admin-main-layout__submenu">
                      {childMenus.map((childMenu) => (
                        <button
                          key={childMenu.key}
                          type="button"
                          className={`admin-main-layout__menu-btn admin-main-layout__submenu-btn${
                            activeMenu === childMenu.key ? ' is-active' : ''
                          }`}
                          onClick={() => {
                            window.scrollTo(0, 0);
                            setActiveMenu(childMenu.key);
                          }}
                        >
                          <span>{childMenu.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <section className="admin-main-layout__content">
          <div className="admin-main-layout__content-body">
            <AdminMainContent activeMenu={activeMenu} adminSession={adminSession} />
          </div>
        </section>
      </div>
    </div>
  );
}
